import { useEffect, useRef, useState } from 'react';

/**
 * An ambient video that plays MUTED on its own and offers one button to hear it.
 *
 * Browsers only allow autoplay without sound, so the element starts muted and
 * looping, and only once it has scrolled into view (so the file is never
 * streamed for visitors who never reach it). Scrolling away pauses it; if it
 * was playing with sound, it goes quiet again rather than singing off-screen.
 *
 * The browser's own controls are on as well (pause, seek, fullscreen, mute).
 * Whatever the visitor does there is mirrored here: unmuting through the
 * control bar behaves like the sound button (plays through once), and a video
 * they paused themselves is left alone when it scrolls back into view.
 *
 *   muted  — looping quietly; button: "Play with sound"
 *   sound  — restarted from the top, unmuted, plays through once; button: "Mute"
 *   ended  — finished with sound; button: "Play again"
 *   paused — prefers-reduced-motion: nothing plays until asked; button: "Play"
 *
 * Props: { src, label, poster?, className? }
 */
export default function SoundToggleVideo({ src, label, poster, className = '' }) {
  const ref = useRef(null);
  // Set right before this component pauses the element itself, so the pause
  // event can tell our pauses from the visitor's.
  const pausingOurselves = useRef(false);
  const userPaused = useRef(false);
  const reduceMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [mode, setMode] = useState(reduceMotion ? 'paused' : 'muted');

  useEffect(() => {
    const video = ref.current;
    if (!video || reduceMotion || typeof IntersectionObserver === 'undefined') return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (video.muted && !userPaused.current) video.play().catch(() => {});
          return;
        }
        if (!video.paused) {
          pausingOurselves.current = true;
          video.pause();
        }
        if (!video.muted) {
          video.muted = true;
          video.loop = true;
        }
      },
      { threshold: 0.4 }
    );
    io.observe(video);
    return () => io.disconnect();
  }, [reduceMotion]);

  function playWithSound() {
    const video = ref.current;
    if (!video) return;
    video.loop = false;
    video.muted = false;
    video.currentTime = 0;
    video.play().catch(() => {});
  }

  function mute() {
    const video = ref.current;
    if (!video) return;
    video.muted = true;
    video.loop = true;
    video.play().catch(() => {});
  }

  // Single source of truth for muted/sound, whichever control changed it.
  function handleVolumeChange() {
    const video = ref.current;
    if (!video) return;
    video.loop = video.muted;
    setMode(video.muted ? 'muted' : 'sound');
  }

  function handlePause() {
    if (pausingOurselves.current) {
      pausingOurselves.current = false;
      return;
    }
    userPaused.current = true;
  }

  function handlePlay() {
    userPaused.current = false;
    if (mode === 'ended' || mode === 'paused') {
      setMode(ref.current?.muted ? 'muted' : 'sound');
    }
  }

  const button =
    mode === 'sound'
      ? { label: 'Mute', onClick: mute, icon: 'on' }
      : mode === 'ended'
        ? { label: 'Play again', onClick: playWithSound, icon: 'replay' }
        : mode === 'paused'
          ? { label: 'Play', onClick: playWithSound, icon: 'play' }
          : { label: 'Play with sound', onClick: playWithSound, icon: 'off' };

  // The control sits over the top of the frame on wide screens, clear of the
  // browser's control bar along the bottom; on phones the frame is short, so
  // it drops below the video instead of covering it.
  return (
    <div className={`relative ${className}`}>
      <div className="relative overflow-hidden rounded-2xl bg-maroon-950 shadow-lift ring-1 ring-maroon-100">
        <video
          ref={ref}
          src={src}
          poster={poster}
          controls
          muted
          loop
          playsInline
          preload="metadata"
          aria-label={label}
          onEnded={() => {
            userPaused.current = false; // running out is not the visitor pausing
            setMode('ended');
          }}
          onVolumeChange={handleVolumeChange}
          onPause={handlePause}
          onPlay={handlePlay}
          className="aspect-video w-full object-cover"
        />
        {mode === 'muted' && (
          <span className="pointer-events-none absolute left-4 top-4 rounded-full bg-maroon-900/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-cream-100">
            Muted
          </span>
        )}
      </div>
      <div className="mt-3 flex justify-center sm:absolute sm:right-4 sm:top-4 sm:mt-0 sm:block">
        <button
          type="button"
          onClick={button.onClick}
          aria-label={`${button.label}: ${label}`}
          className="inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-maroon-800 shadow-lift ring-1 ring-maroon-100 backdrop-blur transition hover:-translate-y-0.5 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400"
        >
          <SpeakerIcon kind={button.icon} />
          {button.label}
        </button>
      </div>
    </div>
  );
}

function SpeakerIcon({ kind }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true' };
  if (kind === 'play') {
    return (
      <svg {...common}>
        <polygon points="6 4 20 12 6 20 6 4" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (kind === 'replay') {
    return (
      <svg {...common}>
        <path d="M3 12a9 9 0 1 0 3-6.7" />
        <polyline points="3 3 3 9 9 9" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      {kind === 'on' ? (
        <>
          <path d="M15.5 8.5a5 5 0 0 1 0 7" />
          <path d="M19 6a9 9 0 0 1 0 12" />
        </>
      ) : (
        <>
          <line x1="22" y1="9" x2="16" y2="15" />
          <line x1="16" y1="9" x2="22" y2="15" />
        </>
      )}
    </svg>
  );
}
